import { Controller } from "@hotwired/stimulus"
import { dia, shapes, V } from '@joint/plus'
import tippy from 'tippy.js'

// Wheel zoom sensitivity — higher = faster.
const WHEEL_ZOOM_SENSITIVITY = 0.0012
// Trackpad pinch (ctrlKey + small pixel deltas) needs a higher gain than mouse
// notches so a useful zoom range takes about one or two pinches (CORE-9584).
const TRACKPAD_PINCH_ZOOM_SENSITIVITY = 0.008
const PINCH_ZOOM_MULTIPLIER = 60
// Cap per-frame wheel delta so one mouse-notch does not jump.
const MAX_WHEEL_DELTA_PER_FRAME = 80
// Pinch events coalesce into larger per-frame totals than a single mouse notch.
const MAX_TRACKPAD_PINCH_DELTA_PER_FRAME = 200
// Ctrl+wheel mouse notches are ~67–100px; trackpad pinch deltas stay under ~50.
const MAX_TRACKPAD_PINCH_WHEEL_DELTA = 60
const ZOOM_VIEWPORT_SYNC_DELAY_MS = 100
// Absolute zoom ceiling; floor is content-aware via minScale().
const MAX_SCALE = 2
const DEFAULT_MIN_SCALE = 0.5
// macOS often fires a brief ctrlKey "pinch" when two-finger pan starts or fingers
// lift. Lock pan for the continuous wheel stream until it goes idle (CORE-9829).
const WHEEL_GESTURE_IDLE_MS = 180
// Swallow a single lone pinch frame so contact/lift blips do not zoom.
const PINCH_ZOOM_CONFIRM_EVENTS = 2

// CORE-9585 — pan room as a fraction of the viewport (was a full viewport per side).
const PAN_MARGIN_X_RATIO = 0.5
const PAN_MARGIN_Y_RATIO = 0.35
// Default sidepanel is 400px; keep a buffer so nodes can clear it when open.
const PAN_MARGIN_X_FLOOR = 480
const PAN_MARGIN_Y_FLOOR = 160
// Initial load: breathing room above the hierarchy root (not flush, not centered).
export const MAP_TOP_INSET_PX = 96

// CORE-8649 — synced vs unsynced affordances (no status badges; fade icon only).
const UNSYNCED_ICON_OPACITY = 0.4
const SYNCED_HOVER_GROW = 1.06
const SYNCED_HOVER_TOOLTIP = "View Device"
const SYNCED_NETWORK_HOVER_TOOLTIP = "View Network"
const UNSYNCED_HOVER_TOOLTIP = "Device not sent to Hudu"
// Icon pads: solid map-colored base + device tint at this opacity so link lines
// cannot show through (transparent fills used to "split" the icon).
const ICON_PAD_TINT_OPACITY = 0.75
// Unsynced pads use system *--50 swatches; synced keep *--200 fills (both tinted).
const UNSYNCED_DEVICE_COLORS = {
  network: "EDE7F6",
  access_point: "F3E5F5",
  firewall: "F1F8E9",
  switch: "FCE4EC",
  router: "F9FBE7",
  printer: "E8EAF6",
  laptop: "E0F2F1",
  workstation: "E0F7FA",
  server: "E1F5FE",
  mobile: "FFF3E0",
  network_device: "FBE9E7",
}

// Keep in sync with NetworkMapsController::NODE_WIDTH / NODE_HEIGHT.
const NODE_WIDTH = 33
const NODE_HEIGHT = 32

// Connects to data-controller="network-map"
export default class extends Controller {
  static values = {
    network: Object,
    previewAssetPath: String,
    previewNetworkPath: String,
    // Per-company map filter prefs (browser localStorage).
    companyId: String,
  }

  static targets = [
    "filtersBar",
    "syncFilterChip",
    "typeFilterChip",
    "clearAll",
    "deviceCount",
    "emptyFilter",
    "viewport",
    "paper",
  ]

  connect() {
    this.initializeProperties()
    this.initializePaper()
    this.setupEventListeners()
    this.setupFilterChips()
    this.draw()
    // Restore may leave sync/type set before the first draw; re-apply so the
    // canvas matches stored prefs without a second user click.
    if (this.hasFiltersBarTarget && (this.syncFilter !== "all" || this.typeFilters.size > 0)) {
      this.applyFilters()
      // Nested filter-chip / filter-option-list connect after this controller, so
      // hoist + chip labels must wait until the next frame.
      requestAnimationFrame(() => this.syncRestoredFilterChipUi())
    }
  }

  disconnect() {
    this.hideNotSentTooltip()
    this.cancelHideNotSentTooltip()

    if (this.viewportSyncFrame) {
      cancelAnimationFrame(this.viewportSyncFrame)
      this.viewportSyncFrame = null
    }
    if (this.zoomFrame) {
      cancelAnimationFrame(this.zoomFrame)
      this.zoomFrame = null
    }
    if (this.zoomViewportSyncTimeout) {
      clearTimeout(this.zoomViewportSyncTimeout)
      this.zoomViewportSyncTimeout = null
    }
    this.isZooming = false

    document.body.style.cursor = ''
    this.parentContainer.style.cursor = ''

    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup',   this._onMouseUp)
    document.removeEventListener('touchmove', this._onTouchMove)
    document.removeEventListener('touchend',  this._onTouchEnd)
    window.removeEventListener('resize', this._onResize)

    this.parentContainer.removeEventListener('scroll', this._onScroll)
    document.removeEventListener('turbo:frame-load', this._onSidepanelFrameLoad)

    this.clearSidepanelLoading()
    // Clear so cancelPreviewPrefetch can abort an in-flight click fetch too.
    this.openingPreviewPath = null
    this.cancelPreviewPrefetch()
    this.cancelSidepanelLoadingOverlaySchedule()

    if (this.paper?.el) {
      this.paper.el.removeEventListener('touchstart', this._onTouchStart)
    }
    this.parentContainer.removeEventListener('wheel', this._onWheel)

    this.layoutObserver?.disconnect()
    this.clearFilterOptionAvailability()
  }

  // ===== INITIALIZATION =====
  
  initializeProperties() {
    this.nodeSpacing = 100
    // Scroll/pan viewport — keep filter chrome outside this element.
    this.parentContainer = this.hasViewportTarget ? this.viewportTarget : this.element
    this.graph = new dia.Graph()
    this.graphScale = 1
    this.renderedNodeIds = new Set()
    this.renderedLinkKeys = new Set()
    // linkKey → link cell. Scanning graph.getLinks() for an existing key made
    // every pan frame quadratic in link count.
    this.linkCells = new Map()
    this.nodeCells = new Map()
    // cell.id → nodeKey so icon/label resolve to the same group without guessing
    this.cellIdToNodeKey = new Map()
    
    // Touch interaction state
    this.isDragging = false
    this.isZooming = false
    this.lastTouchDistance = 0
    this.lastTouchCenter = { x: 0, y: 0 }
    // Trackpad wheel gesture lock — see scrollWheel (CORE-9829).
    this.wheelGestureMode = null
    this.lastWheelAt = 0
    this.pinchZoomStreak = 0
    this.pendingPinchWheel = null
    this.lastZoomTime = 0
    
    // Tooltip state (unsynced / no-slug devices)
    this.currentTooltip = null
    this.tooltipNodeKey = null
    this.tooltipHideTimeout = null
    this.tooltipHideDelayMs = 300
    // Clicking an unsynced node fires a synthetic mouseleave in JointJS/SVG;
    // keep the tooltip up for the duration of that press.
    this.unsyncedPointerDownKey = null
    this.hoveredNodeKey = null
    // Own click detection — JointJS pointerclick drops presses with tiny movement
    // after hover attr updates, which felt like "click does nothing".
    this.pendingClickCellView = null
    this.pendingClickPoint = null
    this.clickMoveThresholdPx = 8

    // Preview prefetch (hover) cache
    this.previewCache = new Map()
    this.prefetchTimeout = null
    this.prefetchAbortController = null
    this.prefetchDelayMs = 100
    this.prefetchPath = null
    this.prefetchPromise = null
    this.openingPreviewPath = null
    this.previewOpenRequestId = 0
    this.sidepanelLoadingOverlayTimeout = null
    // Show the loading chrome on the same tick as the click so the panel never
    // feels like a no-op while Turbo fetches.
    this.sidepanelLoadingOverlayDelayMs = 0

    // Deferred fit when the container is not yet laid out (e.g. hidden turbo frame)
    this.pendingFitToView = false
    this.viewportSyncFrame = null
    this.contentTranslate = { x: 0, y: 0 }

    // Client-side Sync + Type filters.
    this.syncFilter = "all" // all | synced | unsynced
    this.typeFilters = new Set()
    // Set of nodeKey() values that pass the active filters (incl. ancestor
    // keepers). Rebuilt in applyFilters; null when no filters are active.
    this.filterVisibleNodeKeys = null

    // Stimulus Object values JSON.parse on every read, so filter re-layout
    // must mutate a stable tree — not this.networkValue each time.
    this.networkTree = this.networkValue
  }

  initializePaper() {
    const paperEl = this.hasPaperTarget
      ? this.paperTarget
      : (this.parentContainer.querySelector("#my-paper") || this.parentContainer.firstElementChild)

    this.paper = new dia.Paper({
      el: paperEl,
      model: this.graph,
      gridSize: 16,
      drawGrid: { color: this.mapGridDotColor() },
      interactive: false,
      async: true,
    })

    this.dropShadow = this.paper.defineFilter({
      name: 'dropShadow',
      args: {
        dx: 0,
        dy: 4,
        blur: 8,
        color: 'rgba(19, 1, 54, 0.16)'
      }
    })
  }

  // JointJS defaults to #AAAAAA; Text/Neutral light--3 / dark--2 (no text dark--3 token).
  mapGridDotColor() {
    const styles = getComputedStyle(document.documentElement)
    const dark = this.isDarkPortal()
    const token = dark
      ? "--text---neutral---dark--2--default"
      : "--text---neutral---light--3--default"
    const value = styles.getPropertyValue(token).trim()
    if (value) return value
    return dark ? "#ADADAD" : "#616161"
  }

  isDarkPortal() {
    return document.documentElement.classList.contains("dark-portal")
      || document.body?.classList?.contains("dark-portal")
  }

  // Match .network-map__map background so icon bases hide link lines underneath.
  mapBackgroundColor() {
    const styles = getComputedStyle(document.documentElement)
    const token = this.isDarkPortal()
      ? "--background---neutral---dark--2--default"
      : "--background---neutral---light--2--default"
    const value = styles.getPropertyValue(token).trim()
    if (value) return value
    return this.isDarkPortal() ? "#141414" : "#FAFAFA"
  }

  // "#RRGGBB" or "RRGGBB[AA]" → rgba() at the given 0–1 opacity (SVG-safe).
  colorWithOpacity(color, opacity = ICON_PAD_TINT_OPACITY) {
    const raw = String(color || "").replace(/^#/, "")
    const rgb = raw.length >= 6 ? raw.slice(0, 6) : raw
    if (rgb.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(rgb)) return `#${raw}`

    const r = parseInt(rgb.slice(0, 2), 16)
    const g = parseInt(rgb.slice(2, 4), 16)
    const b = parseInt(rgb.slice(4, 6), 16)
    const a = Math.max(0, Math.min(1, opacity))
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }

  setupEventListeners() {
    // Paper events — use pointerdown/up for opens; JointJS pointerclick is flaky
    // after hover style mutations (attr updates recreate SVG under the cursor).
    this.paper.on('cell:pointerdown', (cellView, evt) => this.cellPointerDown(cellView, evt))
    this.paper.on('cell:pointerup', (cellView, evt) => this.cellPointerUp(cellView, evt))
    this.paper.on('cell:mouseenter', (cellView, evt) => this.cellEnter(cellView, evt))
    this.paper.on('cell:mouseleave', (cellView, evt) => this.cellLeave(cellView, evt))
    this.paper.on('blank:pointerdown', (event) => this.pointerDown(event))
    this.paper.on('blank:pointerup', () => this.clearPendingCellClick())

    // Keep the link bookkeeping honest no matter who removes a cell, so a stale
    // key can never make drawLink skip a link that is no longer on the graph.
    this.graph.on('remove', (cell) => {
      const linkKey = cell.get('hudu_link_key')
      if (!linkKey) return

      this.linkCells.delete(linkKey)
      this.renderedLinkKeys.delete(linkKey)
    })

    // Store bound handlers so they can be removed on disconnect
    this._onMouseMove = (e) => this.pointerMove(e)
    this._onMouseUp   = ()  => this.pointerUp()
    this._onTouchStart = (e) => this.touchStart(e)
    this._onTouchMove  = (e) => this.touchMove(e)
    this._onTouchEnd   = (e) => this.touchEnd(e)
    this._onWheel      = (e) => this.scrollWheel(e)
    this._onResize     = ()  => {
      if (this.isDragging) return
      if (this.pendingFitToView && this.hasMeasurableContainer()) {
        this.fitToView()
        this.schedulePinRenderedGraphToTop()
      }
      this.syncViewport()
    }
    this._onScroll     = ()  => {
      if (this.isDragging || this.isZooming) return
      this.scheduleViewportSync()
    }

    document.addEventListener('mousemove', this._onMouseMove)
    document.addEventListener('mouseup',   this._onMouseUp)

    this.paper.el.addEventListener('touchstart', this._onTouchStart, { passive: false })
    document.addEventListener('touchmove', this._onTouchMove, { passive: false })
    document.addEventListener('touchend',  this._onTouchEnd,  { passive: false })

    // Listen on the scroll viewport (not just paper.el) so wheel still zooms/pans
    // when the paper is smaller than the container after zoom-out. passive: false
    // required so preventDefault() is honoured in Chrome.
    this.parentContainer.addEventListener('wheel', this._onWheel, { passive: false })
    window.addEventListener('resize', this._onResize)
    this.parentContainer.addEventListener('scroll', this._onScroll)

    this.parentContainer.style.cursor = 'grab'

    this.setupLayoutObserver()
    this._onSidepanelFrameLoad = (event) => {
      if (event.target.id === 'sidepanel') this.clearSidepanelLoading()
    }
    document.addEventListener('turbo:frame-load', this._onSidepanelFrameLoad)
  }

  hasMeasurableContainer() {
    return this.parentContainer.clientWidth > 0 && this.parentContainer.clientHeight > 0
  }

  setupLayoutObserver() {
    this.layoutObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0 && this.pendingFitToView) {
          this.fitToView()
          this.syncViewport()
          this.schedulePinRenderedGraphToTop()
          break
        }
      }
    })
    this.layoutObserver.observe(this.parentContainer)
  }

  getScrollOffset() {
    return {
      x: this.parentContainer.scrollLeft,
      y: this.parentContainer.scrollTop,
    }
  }

  // Extra scroll room so nodes can be panned into view when the sidepanel is open.
  // Half-viewport (with a sidepanel floor) — enough to clear the panel without a
  // full screen of blank runway on every side (CORE-9585).
  panMargin() {
    const width = this.parentContainer.clientWidth
    const height = this.parentContainer.clientHeight

    return {
      x: Math.max(Math.round(width * PAN_MARGIN_X_RATIO), Math.min(PAN_MARGIN_X_FLOOR, width)),
      y: Math.max(Math.round(height * PAN_MARGIN_Y_RATIO), Math.min(PAN_MARGIN_Y_FLOOR, height)),
    }
  }

  // Inset around the graph bbox for paper sizing / fit-to-view (not tree layout spacing).
  contentPadding() {
    return this.nodeSpacing
  }

  paperDimensionsForScale(scale) {
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight
    const padding = this.contentPadding()
    const margin = this.panMargin()
    const contentWidth = (this.graphBBox.width + padding) * scale
    const contentHeight = (this.graphBBox.height + padding) * scale

    return {
      width: Math.max(containerWidth, contentWidth) + margin.x * 2,
      // Keep a full vertical pan band on both sides so zoom-toward-cursor can
      // push the graph up without making the top unreachable (scrollTop >= 0).
      height: Math.max(containerHeight, contentHeight) + margin.y * 2,
    }
  }

  scheduleViewportSync() {
    if (this.viewportSyncFrame) return

    this.viewportSyncFrame = requestAnimationFrame(() => {
      this.viewportSyncFrame = null
      this.syncViewport()
    })
  }

  nodeKey(node) {
    if (node.id != null) return String(node.id)
    // Slug is unique per record; type:name collapses two synced nodes that share a name.
    if (node.slug) return `${node.type}:${node.slug}`
    if (node.deployment_name) return `${node.type}:${node.name}:${node.deployment_name}`
    return `${node.type}:${node.name}`
  }

  linkKey(sourceNode, targetNode) {
    return `${this.nodeKey(sourceNode)}->${this.nodeKey(targetNode)}`
  }

  registerNodeCells(node, cells) {
    const key = this.nodeKey(node)
    const list = cells.filter(Boolean)
    this.nodeCells.set(key, list)
    list.forEach((cell) => {
      if (cell?.id != null) this.cellIdToNodeKey.set(cell.id, key)
    })
    this.renderedNodeIds.add(key)
  }

  removeNodeByKey(key) {
    const cells = this.nodeCells.get(key) || []
    cells.forEach((cell) => {
      if (cell?.id != null) this.cellIdToNodeKey.delete(cell.id)
      cell.remove()
    })
    this.nodeCells.delete(key)
    this.renderedNodeIds.delete(key)
    // Links are coordinate-based and may still reach a visible neighbor — do not
    // remove them here. syncViewport prunes only when both ends are off-screen.
  }

  // ===== NODE INTERACTIONS =====
  
  // Handle click on network map nodes to open preview in sidepanel
  cellClick(cellView) {
    // Links have no preview — ignore them so a near-miss on a wire never looks
    // like a dead device click.
    if (cellView.model.isLink?.()) return

    const path = this.previewPathForCell(cellView)
    if (!path) {
      // Unsynced devices are not openable. Clicks must not dismiss the tooltip.
      this.keepUnsyncedTooltip(cellView)
      return
    }

    this.cancelPrefetchSchedule()
    this.abortInFlightPrefetch()
    return this.openSidepanelPreview(path)
  }

  cellPointerDown(cellView, evt) {
    if (cellView.model.isLink?.()) return
    if (!cellView.model.attributes.hudu_type) return

    this.pendingClickCellView = cellView
    this.pendingClickPoint = {
      x: evt?.clientX ?? evt?.originalEvent?.clientX ?? 0,
      y: evt?.clientY ?? evt?.originalEvent?.clientY ?? 0,
    }

    if (!cellView.model.attributes.hudu_slug) {
      this.unsyncedPointerDownKey = this.cellKeyFromModel(cellView.model)
      this.keepUnsyncedTooltip(cellView)
    }
  }

  cellPointerUp(cellView, evt) {
    const pending = this.pendingClickCellView
    const origin = this.pendingClickPoint
    this.clearPendingCellClick()

    if (!pending || !cellView) return
    if (pending.model !== cellView.model) return

    const x = evt?.clientX ?? evt?.originalEvent?.clientX ?? origin?.x ?? 0
    const y = evt?.clientY ?? evt?.originalEvent?.clientY ?? origin?.y ?? 0
    if (origin) {
      const dx = Math.abs(x - origin.x)
      const dy = Math.abs(y - origin.y)
      if (dx > this.clickMoveThresholdPx || dy > this.clickMoveThresholdPx) return
    }

    this.cellClick(cellView)
  }

  clearPendingCellClick() {
    this.pendingClickCellView = null
    this.pendingClickPoint = null
    this.unsyncedPointerDownKey = null
  }

  keepUnsyncedTooltip(cellView) {
    this.cancelHideNotSentTooltip()

    // Tippy's default hideOnClick can hide the instance while leaving our
    // reference set — re-show instead of assuming it's still visible.
    if (this.currentTooltip) {
      this.currentTooltip.show?.()
      return
    }

    this.showNotSentTooltip(cellView, !!cellView.model.getParentCell?.())
  }

  openSidepanelPreview(path) {
    // Each click gets an id so a slower earlier open cannot overwrite a newer one.
    const requestId = ++this.previewOpenRequestId
    this.openingPreviewPath = path

    // First open mounts #sidepanel. Later clicks target #sidepanel-content so the
    // shell stays is-visible (same pattern as preview prev/next) — visiting
    // #sidepanel remounts the modal and CSS slide-in runs again every time.
    const alreadyOpen = this.isSidepanelVisible()
    this.prepareSidepanelVisit({ alreadyOpen })

    if (this.previewCache.has(path) && this.renderCachedPreview(path, { alreadyOpen })) {
      this.finishPreviewOpen(path, requestId)
      return
    }

    // Do not await hover prefetch — that race caused "second click" opens when
    // the download was aborted or superseded. Navigate now; Turbo fills the frame.
    if (!this.isCurrentPreviewOpen(requestId)) return

    const frameId = alreadyOpen ? 'sidepanel-content' : 'sidepanel'
    const frame = document.getElementById(frameId)
    const sidepanelFrame = document.getElementById('sidepanel')
    if (sidepanelFrame) sidepanelFrame.dataset.sidepanelOpening = 'true'

    // Same-src Turbo.visit is a no-op on whichever frame we're targeting.
    if (frame?.getAttribute('src') === path) {
      frame.removeAttribute('complete')
      frame.removeAttribute('src')
    }

    this.finishPreviewOpen(path, requestId)
    Turbo.visit(path, { frame: frameId })
  }

  isSidepanelVisible() {
    // Frame is emptied on close; any sidepanel modal means we're swapping, not
    // opening cold (is-visible can lag a tick behind connect).
    const frame = document.getElementById('sidepanel')
    return !!frame?.querySelector('.manage_modal--sidepanel')
  }

  isCurrentPreviewOpen(requestId) {
    return requestId === this.previewOpenRequestId
  }

  finishPreviewOpen(path, requestId) {
    if (this.openingPreviewPath === path && this.isCurrentPreviewOpen(requestId)) {
      this.openingPreviewPath = null
    }
  }

  previewPathForCell(cellView) {
    const slug = cellView.model.attributes.hudu_slug
    if (!slug) return null

    const isNetwork = cellView.model.attributes.hudu_type === "network"
    return (isNetwork ? this.previewNetworkPathValue : this.previewAssetPathValue).replace('slug', slug)
  }

  schedulePreviewPrefetch(cellView) {
    const path = this.previewPathForCell(cellView)
    if (!path || this.previewCache.has(path)) return

    this.cancelPrefetchSchedule()
    this.prefetchTimeout = setTimeout(() => {
      this.prefetchPreview(path).catch(() => {})
    }, this.prefetchDelayMs)
  }

  cancelPrefetchSchedule() {
    if (this.prefetchTimeout) {
      clearTimeout(this.prefetchTimeout)
      this.prefetchTimeout = null
    }
  }

  cancelPreviewPrefetch() {
    this.cancelPrefetchSchedule()
    this.abortInFlightPrefetch()
  }

  abortInFlightPrefetch() {
    if (!this.prefetchAbortController) return

    this.prefetchAbortController.abort()
    this.prefetchAbortController = null
    this.prefetchPath = null
    this.prefetchPromise = null
  }

  prefetchPreview(path) {
    if (this.previewCache.has(path)) return Promise.resolve()

    if (this.prefetchPath === path && this.prefetchPromise) {
      return this.prefetchPromise
    }

    // Hover-only warm cache. Clicks navigate via Turbo.visit and do not await
    // this promise, so aborting on the next hover is safe.
    if (this.prefetchAbortController) {
      this.prefetchAbortController.abort()
      this.prefetchAbortController = null
    }

    this.prefetchPath = path
    const abortController = new AbortController()
    this.prefetchAbortController = abortController

    this.prefetchPromise = fetch(path, {
      signal: abortController.signal,
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html, application/xhtml+xml',
        'Turbo-Frame': 'sidepanel',
        'X-Network-Map-Prefetch': '1',
      },
    }).then(async (response) => {
      if (response.ok) {
        this.previewCache.set(path, await response.text())
      }
    }).catch((error) => {
      if (error.name === 'AbortError') return
      console.debug('Network map preview prefetch failed', error)
    }).finally(() => {
      if (this.prefetchPath === path) {
        this.prefetchPath = null
        this.prefetchPromise = null
      }
      if (this.prefetchAbortController === abortController) {
        this.prefetchAbortController = null
      }
    })

    return this.prefetchPromise
  }

  renderCachedPreview(path, { alreadyOpen = false } = {}) {
    const html = this.previewCache.get(path)
    if (!html) return false

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const fetchedFrame = doc.querySelector('turbo-frame#sidepanel')
    if (!fetchedFrame) return false

    if (alreadyOpen) {
      // Keep the open shell; only swap the inner content frame.
      const contentFrame = document.getElementById('sidepanel-content')
      const fetchedContent = fetchedFrame.querySelector('turbo-frame#sidepanel-content')
      if (!contentFrame || !fetchedContent) return false

      contentFrame.innerHTML = fetchedContent.innerHTML
      contentFrame.dispatchEvent(new CustomEvent('turbo:frame-load', { bubbles: true }))
    } else {
      const frame = document.getElementById('sidepanel')
      if (!frame) return false

      // Keep sidepanelOpening set across the HTML swap. turbo-modal reads it on
      // connect to skip the slide-in; clearing it here raced Stimulus connect and
      // caused intermittent re-animation when clicking between nodes.
      frame.dataset.sidepanelOpening = 'true'
      frame.innerHTML = fetchedFrame.innerHTML
      frame.dispatchEvent(new CustomEvent('turbo:frame-load', { bubbles: true }))
    }

    this.cancelSidepanelLoadingOverlaySchedule()
    this.removeSidepanelLoadingOverlay()
    this.recordPreviewOpen(path)
    return true
  }

  recordPreviewOpen(path) {
    fetch(path, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'Turbo-Frame': 'sidepanel',
        'X-Network-Map-Log-Only': '1',
      },
    }).catch(() => {})
  }

  prepareSidepanelVisit({ alreadyOpen = false } = {}) {
    const frame = document.getElementById('sidepanel')
    if (frame) frame.dataset.sidepanelOpening = 'true'

    // Skip the loading overlay when swapping between devices — it made an
    // already-open panel look like it was closing and reopening.
    if (!alreadyOpen) {
      this.scheduleSidepanelLoadingOverlay()
    }
  }

  scheduleSidepanelLoadingOverlay() {
    this.cancelSidepanelLoadingOverlaySchedule()
    this.sidepanelLoadingOverlayTimeout = setTimeout(
      () => this.showSidepanelLoadingOverlay(),
      this.sidepanelLoadingOverlayDelayMs,
    )
  }

  cancelSidepanelLoadingOverlaySchedule() {
    if (this.sidepanelLoadingOverlayTimeout) {
      clearTimeout(this.sidepanelLoadingOverlayTimeout)
      this.sidepanelLoadingOverlayTimeout = null
    }
  }

  showSidepanelLoadingOverlay() {
    this.removeSidepanelLoadingOverlay()

    const overlay = document.createElement('div')
    overlay.className = 'network-map__sidepanel-loading-overlay manage_modal manage_modal--sidepanel is-visible'
    overlay.dataset.networkMapSidepanelLoading = 'true'
    overlay.innerHTML = `
      <header>
        <h1>Loading...</h1>
      </header>
      <div class="manage__modal__body">
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
    `
    document.body.appendChild(overlay)
    this.sidepanelLoadingOverlay = overlay
  }

  removeSidepanelLoadingOverlay() {
    this.sidepanelLoadingOverlay?.remove()
    this.sidepanelLoadingOverlay = null
  }

  clearSidepanelLoading() {
    // Do not clear data-sidepanel-opening here — turbo-modal consumes it on
    // connect to skip slide-in. Clearing early caused intermittent re-animation.
    this.cancelSidepanelLoadingOverlaySchedule()
    this.removeSidepanelLoadingOverlay()
  }

  // Apply hover effects when mouse enters a node
  cellEnter(cellView, _evt) {
    const model = cellView.model
    if (model.isLink?.()) return

    const parentModel = model.getParentCell()
    const type = model.attributes.hudu_type
    const slug = model.attributes.hudu_slug

    if (!type) return

    if (slug) {
      this.cancelHideNotSentTooltip()
      const nodeKey = this.cellKeyFromModel(model)
      // Re-applying attrs mid-hover recreates SVG nodes under the cursor and
      // breaks the following click. Only style when entering a new node.
      if (nodeKey !== this.hoveredNodeKey) {
        this.hoveredNodeKey = nodeKey
        this.applyHoverStyles(model, type)
        this.showDeviceHoverTooltip(cellView, !!parentModel, this.syncedHoverTooltip(type))
      }
      this.schedulePreviewPrefetch(cellView)
      return
    }

    // Unsynced tooltip — ignore leave→enter flicker on the same nodeKey.
    this.cancelHideNotSentTooltip()
    const nodeKey = this.cellKeyFromModel(model)
    if (nodeKey && this.tooltipNodeKey === nodeKey && this.currentTooltip) {
      this.updateNotSentTooltipPosition(cellView, !!parentModel)
      return
    }

    this.showNotSentTooltip(cellView, !!parentModel)
  }

  // Remove hover effects when mouse leaves a node
  cellLeave(cellView, evt) {
    const model = cellView.model
    if (model.isLink?.()) return

    const type = model.attributes.hudu_type

    if (!type) return

    const slug = model.attributes.hudu_slug

    // Device icon+name are one cell now, so leave means the cursor left the whole node.
    if (slug) {
      if (this.isPointerStillOnSameNode(cellView, evt)) return

      this.hoveredNodeKey = null
      this.clearHoverStyles(model, type)
      this.hideNotSentTooltip()
      this.cancelPreviewPrefetch()
      return
    }

    // Nodes are one cell now; keep this guard for leave→enter flicker on the same key.
    if (this.isPointerStillOnSameNode(cellView, evt)) return

    const nodeKey = this.cellKeyFromModel(model)
    // Pressing on an unsynced node synthesizes a leave — do not hide mid-click.
    if (this.unsyncedPointerDownKey && this.unsyncedPointerDownKey === nodeKey) return

    this.scheduleHideNotSentTooltip(nodeKey)
  }

  isPointerStillOnSameNode(cellView, evt) {
    const nodeKey = this.cellKeyFromModel(cellView.model)
    if (!nodeKey || !evt?.relatedTarget || !this.paper) return false

    const nextView = this.paper.findView?.(evt.relatedTarget)
    if (!nextView?.model || nextView.model.isLink?.()) return false

    return this.cellKeyFromModel(nextView.model) === nodeKey
  }

  applyHoverStyles(model, type) {
    const hover = this.colorWithOpacity(this.getDeviceHoverColor(type))
    const filter = `url(#${this.dropShadow})`

    model.attr("body/filter", filter)
    model.attr("body/fill", hover)
    model.attr("body/stroke", hover)

    const attrs = model.get?.("attrs") || model.attributes?.attrs || {}
    if (attrs.nameBody) {
      model.attr("nameBody/filter", filter)
    }

    const embedded = model.getEmbeddedCells?.() || []
    embedded.forEach((cell) => cell.attr("body/filter", filter))

    this.applyHoverGrow(model)
  }

  clearHoverStyles(model, type) {
    if (!model || !type) return

    // Tinted body is fine when bodyBase (map-colored plate) sits underneath and
    // hides link lines. Networks use the hover swatch for stroke; devices match fill.
    const fill = this.colorWithOpacity(this.getDeviceColor(type))
    const stroke = type === "network"
      ? this.colorWithOpacity(this.getDeviceHoverColor(type))
      : fill

    model.attr("body/filter", null)
    model.attr("body/fill", fill)
    model.attr("body/stroke", stroke)

    const attrs = model.get?.("attrs") || model.attributes?.attrs || {}
    if (attrs.nameBody) {
      model.attr("nameBody/filter", null)
    }

    const embedded = model.getEmbeddedCells?.() || []
    embedded.forEach((cell) => cell.attr("body/filter", null))

    this.clearHoverGrow(model)
  }

  // Slight grow around the cell center so synced devices feel clickable (CORE-8649).
  applyHoverGrow(model) {
    const size = model.size?.() || model.attributes?.size
    if (!size?.width || !size?.height) return

    const cx = size.width / 2
    const cy = size.height / 2
    const transform = `translate(${cx},${cy}) scale(${SYNCED_HOVER_GROW}) translate(${-cx},${-cy})`
    this.hoverGrowSelectors().forEach((selector) => {
      model.attr(`${selector}/transform`, transform)
    })
  }

  clearHoverGrow(model) {
    this.hoverGrowSelectors().forEach((selector) => {
      model.attr(`${selector}/transform`, null)
    })
  }

  hoverGrowSelectors() {
    return ["hitArea", "bodyBase", "body", "icon", "label", "nameBase", "nameBody", "nameText"]
  }

  cellKeyFromModel(model) {
    const key = model.attributes?.hudu_node_key
    if (key) return key

    const parentModel = model.getParentCell?.()
    if (parentModel?.attributes?.hudu_node_key) {
      return parentModel.attributes.hudu_node_key
    }

    const labelModel = model.getEmbeddedCells?.()[0]
    if (labelModel?.attributes?.hudu_node_key) {
      return labelModel.attributes.hudu_node_key
    }

    return null
  }

  scheduleHideNotSentTooltip(nodeKey) {
    this.cancelHideNotSentTooltip()

    this.tooltipHideTimeout = setTimeout(() => {
      this.tooltipHideTimeout = null
      if (nodeKey && this.tooltipNodeKey === nodeKey) {
        this.hideNotSentTooltip()
      }
    }, this.tooltipHideDelayMs)
  }

  cancelHideNotSentTooltip() {
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout)
      this.tooltipHideTimeout = null
    }
  }

  // ===== TOOLTIP METHODS =====

  notSentTooltipPagePoint(cellView, isLabelCell) {
    const model = cellView.model
    const bbox = model.getBBox()
    const scale = this.graphScale
    const centerGraphPoint = bbox.center()
    const offsetY = 10 / scale

    const aboveGraphPoint = centerGraphPoint.clone()
    aboveGraphPoint.y = bbox.y - offsetY + (isLabelCell ? -31 : 6)

    return this.paper.localToPagePoint(aboveGraphPoint)
  }

  updateNotSentTooltipPosition(cellView, isLabelCell) {
    if (!this.currentTooltip?.reference) return

    const pagePoint = this.notSentTooltipPagePoint(cellView, isLabelCell)
    const element = this.currentTooltip.reference
    element.style.left = `${pagePoint.x}px`
    element.style.top = `${pagePoint.y}px`
    this.currentTooltip.popperInstance?.update()
  }
  
  showDeviceHoverTooltip(cellView, isLabelCell, content) {
    this.hideNotSentTooltip()

    const pagePoint = this.notSentTooltipPagePoint(cellView, isLabelCell)

    const tooltipElement = document.createElement('div')
    tooltipElement.style.position = 'absolute'
    tooltipElement.style.left = `${pagePoint.x}px`
    tooltipElement.style.top = `${pagePoint.y}px`
    tooltipElement.style.pointerEvents = 'none'
    tooltipElement.style.zIndex = '1000'
    document.body.appendChild(tooltipElement)

    // hideOnClick false — tippy defaults to hiding on any click, which made the
    // "not sent" message vanish when pressing an unsynced device. duration 0
    // avoids fade flicker on leave/enter.
    this.currentTooltip = tippy(tooltipElement, {
      content,
      theme: 'light',
      placement: 'top',
      arrow: true,
      interactive: false,
      trigger: 'manual',
      showOnCreate: true,
      hideOnClick: false,
      duration: 0,
      appendTo: () => document.body,
    })

    this.tooltipNodeKey = this.cellKeyFromModel(cellView.model)
  }

  // Unsynced (no-slug) hover/click tooltip — shared entry from cellEnter / keepUnsyncedTooltip.
  showNotSentTooltip(cellView, isLabelCell) {
    this.showDeviceHoverTooltip(cellView, isLabelCell, UNSYNCED_HOVER_TOOLTIP)
  }
  
  hideNotSentTooltip() {
    this.cancelHideNotSentTooltip()
    this.tooltipNodeKey = null

    if (this.currentTooltip) {
      const element = this.currentTooltip.reference;
      this.currentTooltip.destroy();
      this.currentTooltip = null;
      
      // Remove the element from the DOM
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
  }

  // ===== MOUSE INTERACTIONS =====

  pointerDown(e) {
    this.isDragging = true
    this.startX = e.clientX
    this.startY = e.clientY
    this.scrollLeft = this.parentContainer.scrollLeft
    this.scrollTop = this.parentContainer.scrollTop

    this.parentContainer.style.cursor = 'grabbing'
    document.body.style.cursor = 'grabbing'
  }

  pointerMove(e) {
    if (!this.isDragging) return

    const x = e.clientX - this.startX
    const y = e.clientY - this.startY
    this.parentContainer.scrollLeft = this.scrollLeft - x
    this.parentContainer.scrollTop = this.scrollTop - y
    this.scheduleViewportSync()
  }

  pointerUp() {
    this.isDragging = false
    this.parentContainer.style.cursor = 'grab'
    document.body.style.cursor = ''
    this.syncViewport()
  }

  // ===== TOUCH INTERACTIONS =====
  
  // Handle touch start events for mobile panning and pinch-to-zoom
  touchStart(e) {
    if (e.touches.length === 1) {
      this.isDragging = true
      this.isZooming = false
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      const offset = this.getScrollOffset()
      this.scrollLeft = offset.x
      this.scrollTop = offset.y
    } else if (e.touches.length === 2) {
      e.preventDefault()
      this.isDragging = false
      this.isZooming = true
      this.lastTouchDistance = this.getTouchDistance(e.touches)
      this.lastTouchCenter = this.getTouchCenter(e.touches)
    }
  }

  // Handle touch movement for panning and pinch-to-zoom gestures
  touchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      
      if (!this.isZooming) {
        this.isDragging = false
        this.isZooming = true
        this.lastTouchDistance = this.getTouchDistance(e.touches)
        this.lastTouchCenter = this.getTouchCenter(e.touches)
        return
      }
      
      this.handlePinchZoom(e.touches)
    } else if (this.isDragging && e.touches.length === 1) {
      e.preventDefault()
      const x = e.touches[0].clientX - this.startX
      const y = e.touches[0].clientY - this.startY
      this.parentContainer.scrollLeft = this.scrollLeft - x
      this.parentContainer.scrollTop = this.scrollTop - y
      this.scheduleViewportSync()
    }
  }

  // Handle touch end events and state transitions
  touchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false
      this.isZooming = false
      this.syncViewport()
    } else if (e.touches.length === 1) {
      this.isZooming = false
      this.isDragging = true
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      const offset = this.getScrollOffset()
      this.scrollLeft = offset.x
      this.scrollTop = offset.y
    }
  }

  // Calculate distance between two touch points for pinch gestures
  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Calculate center point between two touch points for zoom focus
  getTouchCenter(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    }
  }

  // Handle pinch-to-zoom gesture
  handlePinchZoom(touches) {
    const currentDistance = this.getTouchDistance(touches)
    const currentCenter = this.getTouchCenter(touches)
    
    if (this.lastTouchDistance > 0) {
      const scale = currentDistance / this.lastTouchDistance
      const clampedScale = Math.max(0.5, Math.min(2.0, scale))
      const zoomFactor = (1 - clampedScale) * 0.5
      
      const currentTime = Date.now()
      const timeSinceLastZoom = currentTime - this.lastZoomTime
      
      if (Math.abs(zoomFactor) > 0.01 && timeSinceLastZoom > 16) {
        const syntheticEvent = {
          deltaY: zoomFactor * PINCH_ZOOM_MULTIPLIER,
          clientX: currentCenter.x,
          clientY: currentCenter.y,
          preventDefault: () => {}
        }
        
        this.applyZoom(syntheticEvent)
        this.lastZoomTime = currentTime
      }
    }
    
    this.lastTouchDistance = currentDistance
    this.lastTouchCenter = currentCenter
  }

  // ===== ZOOM FUNCTIONALITY =====

  // Handle mouse wheel and trackpad scroll/pinch events.
  // Heuristic to distinguish zoom vs pan (no keyboard modifier required):
  //   ctrlKey=true              → zoom (trackpad pinch or Ctrl+wheel; pinch
  //                               vs mouse sensitivity is decided in queueZoom)
  //   deltaMode !== 0           → line/page mode (physical mouse wheel) → zoom
  //   deltaX === 0 && |deltaY| >= 50 → large pure-vertical pixel delta  → zoom
  //   everything else           → trackpad two-finger scroll            → pan
  //
  // Lock pan for the continuous wheel stream so a brief ctrlKey blip when fingers
  // lift mid two-finger pan cannot switch into pinch zoom (CORE-9829). Intentional
  // trackpad pinch needs two consecutive pinch frames — a single contact/lift
  // frame is ignored. Zoom mode only continues while ctrlKey (or mouse-wheel)
  // events keep arriving; a following non-ctrl pan frame releases zoom.
  //
  // Confirmed pinch (2+ frames) or a clear mouse-wheel zoom may break out of an
  // active pan lock — otherwise inertial pan after scale=1 load swallows zoom.
  scrollWheel(e) {
    e.preventDefault()

    const now = performance.now()
    if (now - this.lastWheelAt > WHEEL_GESTURE_IDLE_MS) {
      this.wheelGestureMode = null
      this.pinchZoomStreak = 0
      this.pendingPinchWheel = null
    }
    this.lastWheelAt = now

    const isMouseWheelZoom = e.deltaMode !== 0
      || (e.deltaX === 0 && Math.abs(e.deltaY) >= 50)
    const isPinchCandidate = !!e.ctrlKey && e.deltaMode === 0 && !isMouseWheelZoom

    if (this.wheelGestureMode === 'pan') {
      // Single ctrlKey blip during pan: absorb as pan (CORE-9829).
      // Confirmed pinch or mouse-wheel zoom: break out so zoom is not stuck.
      if (isMouseWheelZoom) {
        this.wheelGestureMode = 'zoom'
        this.pinchZoomStreak = 0
        this.pendingPinchWheel = null
        this.queueZoom(e)
        return
      }
      if (isPinchCandidate) {
        this.pinchZoomStreak += 1
        if (this.pinchZoomStreak < PINCH_ZOOM_CONFIRM_EVENTS) {
          this.panFromWheel(e)
          return
        }
        this.wheelGestureMode = 'zoom'
        this.queueZoom(e)
        return
      }
      this.pinchZoomStreak = 0
      this.pendingPinchWheel = null
      this.panFromWheel(e)
      return
    }

    // Mouse wheel, or an in-progress pinch that still has ctrlKey.
    if (isMouseWheelZoom || (this.wheelGestureMode === 'zoom' && e.ctrlKey)) {
      this.wheelGestureMode = 'zoom'
      this.pinchZoomStreak = 0
      this.pendingPinchWheel = null
      this.queueZoom(e)
      return
    }

    if (isPinchCandidate) {
      this.pinchZoomStreak += 1
      if (this.pinchZoomStreak < PINCH_ZOOM_CONFIRM_EVENTS) {
        // Hold the first frame; contact/lift noise rarely sends a second.
        this.pendingPinchWheel = {
          clientX: e.clientX,
          clientY: e.clientY,
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          ctrlKey: e.ctrlKey,
        }
        return
      }
      this.wheelGestureMode = 'zoom'
      if (this.pendingPinchWheel) {
        this.queueZoom(this.pendingPinchWheel)
        this.pendingPinchWheel = null
      }
      this.queueZoom(e)
      return
    }

    // Includes post-pinch non-ctrl frames — do not keep zooming until idle.
    this.pinchZoomStreak = 0
    this.pendingPinchWheel = null
    this.wheelGestureMode = 'pan'
    this.panFromWheel(e)
  }

  panFromWheel(e) {
    this.parentContainer.scrollLeft += e.deltaX
    this.parentContainer.scrollTop += e.deltaY
    this.scheduleViewportSync()
  }

  // Coalesce wheel events to one zoom step per animation frame for smoother feel.
  queueZoom(e) {
    this.pendingZoomDelta = (this.pendingZoomDelta || 0) + this.normalizedWheelDelta(e)
    this.pendingZoomPoint = { clientX: e.clientX, clientY: e.clientY }
    // macOS trackpad pinch arrives as ctrlKey + small pixel deltas. Ctrl+wheel
    // on Windows also sets ctrlKey but with large notches — exclude those.
    // OR across coalesced events so a mixed frame still uses pinch limits.
    const isPinchEvent = !!e.ctrlKey
      && e.deltaMode === 0
      && Math.abs(e.deltaY) < MAX_TRACKPAD_PINCH_WHEEL_DELTA
    this.pendingZoomIsTrackpadPinch = this.pendingZoomIsTrackpadPinch || isPinchEvent

    if (this.zoomFrame) return

    this.zoomFrame = requestAnimationFrame(() => {
      this.zoomFrame = null
      this.flushZoom()
    })
  }

  flushZoom() {
    const point = this.pendingZoomPoint
    let delta = this.pendingZoomDelta || 0
    const isTrackpadPinch = !!this.pendingZoomIsTrackpadPinch
    this.pendingZoomDelta = 0
    this.pendingZoomPoint = null
    this.pendingZoomIsTrackpadPinch = false

    if (!point || delta === 0) return

    const maxDelta = isTrackpadPinch
      ? MAX_TRACKPAD_PINCH_DELTA_PER_FRAME
      : MAX_WHEEL_DELTA_PER_FRAME
    delta = Math.max(-maxDelta, Math.min(maxDelta, delta))
    this.applyZoom({ ...point, deltaY: delta, deltaMode: 0, isTrackpadPinch })
  }

  // Apply a zoom step centred on the cursor/touch position
  applyZoom(e) {
    const oldScale = this.graphScale
    const newScale = this.clampScale(this.scaleFromWheelDelta(oldScale, e))

    if (newScale === oldScale) return

    this.graphScale = newScale
    // scalePaper keeps the cursor graph point fixed. Do not reframe after zoom-out —
    // that overwrote scroll and snapped overflowing maps to center (CORE-9585 review).
    this.scalePaper(e, oldScale)
    // Reveal newly visible nodes during zoom-out; defer cull until settle so the
    // map does not blank then pop back in.
    this.syncViewport({ cull: false })
    this.scheduleZoomViewportSync()
  }

  scheduleZoomViewportSync() {
    if (this.zoomViewportSyncTimeout) {
      clearTimeout(this.zoomViewportSyncTimeout)
    }

    // Full cull only after zooming settles — remove/add every wheel tick feels bumpy.
    this.zoomViewportSyncTimeout = setTimeout(() => {
      this.zoomViewportSyncTimeout = null
      this.isZooming = false
      this.syncViewport()
    }, ZOOM_VIEWPORT_SYNC_DELAY_MS)
  }

  // Scale that would fit the full graph (with padding) in the viewport.
  // Used by minScale() for the zoom-out floor; fitToView no longer applies this.
  // null when bbox/container aren't ready yet.
  fitIdealScale() {
    if (!this.graphBBox || !this.parentContainer) return null

    const padding = this.contentPadding()
    const contentWidth = this.graphBBox.width + padding
    const contentHeight = this.graphBBox.height + padding
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight

    if (contentWidth <= 0 || contentHeight <= 0) return null
    if (containerWidth <= 0 || containerHeight <= 0) return null

    return Math.min(containerWidth / contentWidth, containerHeight / contentHeight)
  }

  // Small maps keep the 0.5 floor; wide/tall maps may go lower so the full
  // tree can fit on screen.
  minScale() {
    const ideal = this.fitIdealScale()
    if (ideal == null) return DEFAULT_MIN_SCALE
    return Math.min(DEFAULT_MIN_SCALE, ideal)
  }

  clampScale(scale) {
    return Math.min(MAX_SCALE, Math.max(this.minScale(), scale))
  }

  normalizedWheelDelta(e) {
    let delta = e.deltaY

    if (e.deltaMode === 1) {
      delta *= 16
    } else if (e.deltaMode === 2) {
      delta *= this.parentContainer.clientHeight || 600
    }

    return delta
  }

  scaleFromWheelDelta(currentScale, e) {
    const delta = this.normalizedWheelDelta(e)
    const sensitivity = e.isTrackpadPinch
      ? TRACKPAD_PINCH_ZOOM_SENSITIVITY
      : WHEEL_ZOOM_SENSITIVITY
    return currentScale * Math.exp(-delta * sensitivity)
  }

  // Zoom in the network map (used by tests and pinch fallback)
  zoomIn(e) {
    this.applyZoom({ ...e, deltaY: e.deltaY ?? -33 })
  }

  // Zoom out the network map (used by tests and pinch fallback)
  zoomOut(e) {
    this.applyZoom({ ...e, deltaY: e.deltaY ?? 33 })
  }

  // Apply zoom scaling to the paper while keeping the cursor's graph point fixed
  // on screen (zoom-toward-cursor).
  scalePaper(e, oldScale) {
    // Stays true until scheduleZoomViewportSync so scroll-driven culls cannot wipe
    // cells between pinch frames.
    this.isZooming = true

    const newScale = this.graphScale
    const { width, height } = this.paperDimensionsForScale(newScale)

    const containerRect = this.parentContainer.getBoundingClientRect()
    const offsetX = e.clientX - containerRect.left
    const offsetY = e.clientY - containerRect.top
    const paperX = this.parentContainer.scrollLeft + offsetX
    const paperY = this.parentContainer.scrollTop + offsetY
    const { x: translateX, y: translateY } = this.contentTranslate

    const graphX = (paperX - translateX) / oldScale
    const graphY = (paperY - translateY) / oldScale

    this.paper.scale(newScale, newScale)
    // paperDimensionsForScale already includes newScale — do not multiply again.
    this.paper.setDimensions(width, height)

    this.parentContainer.scrollLeft = (graphX * newScale) + translateX - offsetX
    this.parentContainer.scrollTop = (graphY * newScale) + translateY - offsetY
  }

  updatePaperDimensionsForScale() {
    this.sizeTree()
  }

  // ===== RENDERING =====

  // Initial draw of the network map
  draw() {
    this.sizeTree()
    // Fit first: sizing and centering read graphBBox (raw data), not the graph,
    // so there is no need to render the whole tree only to cull it a frame later.
    // Fitting up front also means the first sync uses the final scale/translate.
    this.fitToView()
    this.syncViewport()
    // After paint: pin to the top inset, then re-cull so viewport matches the shift.
    this.schedulePinRenderedGraphToTop()
  }

  // Backwards-compatible alias used by tests
  redraw() {
    this.syncViewport()
  }

  // cull: false — add newly visible nodes/links only (used while pinching so
  // zoom-out does not wait for the settle timer to refill the map).
  syncViewport({ renderAll = false, cull = true } = {}) {
    if (!this.paper?.el || !this.graphBBox) return

    const viewport = renderAll ? this.getFullGraphViewport() : this.getViewportBounds()
    const shouldBeVisible = new Set()

    const walk = (node, parent) => {
      if (node.type === 'internet') {
        node.children?.forEach((child) => walk(child, node))
        return
      }

      const key = this.nodeKey(node)
      const passesFilters = this.shouldRenderNode(node)

      if (passesFilters && this.isBoxVisible(node, viewport)) {
        shouldBeVisible.add(key)

        if (!this.renderedNodeIds.has(key)) {
          if (node.type === 'network') {
            this.drawNetworkBox(node)
          } else {
            this.drawDeviceBox(node)
          }
        }
      }

      node.children?.forEach((child) => walk(child, node))
    }

    walk(this.networkTree, null)

    if (cull) {
      for (const key of [...this.renderedNodeIds]) {
        if (!shouldBeVisible.has(key)) {
          this.removeNodeByKey(key)
        }
      }

      // Drop edges only when both ends are off-screen; then restore any missing
      // edges that still touch a visible node (lines into/out of the viewport).
      this.pruneDetachedLinks(viewport)
    }

    this.ensureVisibleLinks(viewport)
  }

  // ===== FILTERS (design-system FilterChipComponent: Sync + Type) =====

  setupFilterChips() {
    if (!this.hasFiltersBarTarget) return

    this.restoreFiltersFromStorage()
    this.syncFiltersFromChips()
    this.updateDeviceCount()
    this.updateFilterChipActiveState()
    // Gray out zero-match options on first paint (e.g. Synced on an unsynced-only map).
    this.updateFilterAvailability()
  }

  filterStorageKey() {
    if (!this.hasCompanyIdValue || !this.companyIdValue) return null
    return `huduNetworkMapFilters:${this.companyIdValue}`
  }

  // Persist Sync + Type for this company map only (not account-wide).
  saveFiltersToStorage() {
    const key = this.filterStorageKey()
    if (!key) return

    const active = this.syncFilter !== "all" || this.typeFilters.size > 0
    if (!active) {
      this.removeFiltersFromStorage()
      return
    }

    try {
      localStorage.setItem(key, JSON.stringify({
        sync: this.syncFilter,
        types: [...this.typeFilters],
      }))
    } catch {
      // Best-effort; filtering still works when storage is unavailable.
    }
  }

  removeFiltersFromStorage() {
    const key = this.filterStorageKey()
    if (!key) return

    try {
      localStorage.removeItem(key)
    } catch {
      // Best-effort; Clear all still resets in-memory filters.
    }
  }

  restoreFiltersFromStorage() {
    const key = this.filterStorageKey()
    if (!key) return false

    try {
      const stored = localStorage.getItem(key)
      if (!stored) return false

      const parsed = JSON.parse(stored)
      const sync = ["all", "synced", "unsynced"].includes(parsed?.sync) ? parsed.sync : "all"
      const types = Array.isArray(parsed?.types) ? parsed.types.filter(Boolean) : []
      if (sync === "all" && types.length === 0) return false

      this.syncFilter = sync
      this.applySyncFilterToChip(sync)

      if (this.hasTypeFilterChipTarget) {
        const available = new Set(
          [...this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]')]
            .map((input) => input.value)
            .filter(Boolean),
        )
        const restoredTypes = types.filter((type) => available.has(type))
        this.typeFilters = new Set(restoredTypes)
        this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = this.typeFilters.has(input.value)
        })
        // Hoist + chip labels run in syncRestoredFilterChipUi() after nested
        // FilterChip controllers finish connecting (see connect()).
      }

      return this.syncFilter !== "all" || this.typeFilters.size > 0
    } catch {
      return false
    }
  }

  // After reload, pin restored selections to the top and restore chip labels.
  syncRestoredFilterChipUi() {
    if (this.syncFilter !== "all") {
      this.applySyncFilterToChip(this.syncFilter)
      this.updateSyncFilterChipTriggerLabel()
      this.rehomeSyncFilterList()
    }

    if (this.hasTypeFilterChipTarget && this.typeFilters.size > 0) {
      this.rehomeTypeFilterList()
      this.updateTypeFilterChipTriggerLabel()

      const panel = this.typeFilterChipTarget.querySelector(".filter-chip__panel--value")
      panel?.dispatchEvent(new CustomEvent("filter-option-list:change", {
        bubbles: true,
        detail: { selected: [...this.typeFilters] },
      }))
    }

    // Re-apply unavailable styling after list DOM is rebuilt.
    this.updateFilterAvailability()
  }

  applySyncFilterToChip(sync) {
    if (!this.hasSyncFilterChipTarget) return

    // Idle = no selection (no All option). Only synced/unsynced select a row.
    const next = ["synced", "unsynced"].includes(sync) ? sync : null
    this.syncFilterChipTarget.querySelectorAll('[role="option"][data-value]').forEach((row) => {
      row.setAttribute(
        "aria-selected",
        next && row.dataset.value === next ? "true" : "false",
      )
    })
  }

  // Attribute label + optional (1). Dismiss visibility is FilterChip's CSS :has().
  updateSyncFilterChipTriggerLabel() {
    if (!this.hasSyncFilterChipTarget) return

    const label = this.syncFilterChipTarget.querySelector("[data-filter-chip-target='label']")
    if (!label) return

    const count = this.syncFilter === "all" ? 0 : 1
    const attribute = label.textContent.replace(/\s*\(\d+\)\s*$/, "").trim() || "Sync status"
    label.textContent = count === 0 ? attribute : `${attribute} (${count})`

    const chipEl = this.syncFilterChipTarget.querySelector("[data-controller~='filter-chip']")
    const chip = chipEl
      && this.application.getControllerForElementAndIdentifier(chipEl, "filter-chip")
    if (chip) chip.valueCount = count
  }

  updateTypeFilterChipTriggerLabel() {
    if (!this.hasTypeFilterChipTarget) return

    const label = this.typeFilterChipTarget.querySelector("[data-filter-chip-target='label']")
    if (!label) return

    const count = this.typeFilters.size
    const attribute = label.textContent.replace(/\s*\(\d+\)\s*$/, "").trim() || "Device type"
    label.textContent = count === 0 ? attribute : `${attribute} (${count})`

    const chipEl = this.typeFilterChipTarget.querySelector("[data-controller~='filter-chip']")
    const chip = chipEl
      && this.application.getControllerForElementAndIdentifier(chipEl, "filter-chip")
    if (chip) chip.valueCount = count
  }

  // Option-list owns rehoming: toggle/selectSingle (live), hoistOnClose (panel
  // close), and resetIfOwner (filter-chip:cleared). Do not rehome here.
  onFilterChipChanged(event) {
    if (!this.element.contains(event.target)) return
    this.syncFiltersFromChips()
    this.applyFilters()
  }

  onFilterChipCleared(event) {
    if (!this.element.contains(event.target)) return
    this.syncFiltersFromChips()
    this.applyFilters()
  }

  // Live-update the map while a chip panel is still open so active filters
  // are obvious before the user clicks away.
  onFilterOptionListChange(event) {
    if (!this.element.contains(event.target)) return
    this.syncFiltersFromChips()
    this.applyFilters()
  }

  syncFiltersFromChips() {
    this.syncSyncFilterFromChip()
    this.syncTypeFiltersFromChip()
  }

  syncSyncFilterFromChip() {
    const next = this.readSyncFilterFromChip()
    // Ignore a pick on an unavailable option (pointer-events usually blocks it).
    if (next !== "all") {
      const row = this.syncFilterChipTarget?.querySelector(
        `[role="option"][data-value="${next}"]`,
      )
      if (this.isFilterOptionDisabled(row)) {
        this.applySyncFilterToChip(this.syncFilter)
        return
      }
    }
    this.syncFilter = next
  }

  readSyncFilterFromChip() {
    if (!this.hasSyncFilterChipTarget) return "all"

    const selected = this.syncFilterChipTarget.querySelector(
      '[role="option"][aria-selected="true"]',
    )
    const value = selected?.dataset.value
    // No selection (dismiss / idle) means show everything — same as former "All".
    return ["synced", "unsynced"].includes(value) ? value : "all"
  }

  // Pin checked Type options to a synthetic top section; unchecked return to
  // their abc home order. Delegates to FilterChip's filter-option-list#rehome.
  rehomeTypeFilterList() {
    this.rehomeFilterList(this.hasTypeFilterChipTarget ? this.typeFilterChipTarget : null)
  }

  rehomeSyncFilterList() {
    this.rehomeFilterList(this.hasSyncFilterChipTarget ? this.syncFilterChipTarget : null)
  }

  rehomeFilterList(wrap) {
    if (!wrap) return

    const listEl = wrap.querySelector("[data-controller~='filter-option-list']")
    if (!listEl) return

    const list = this.application.getControllerForElementAndIdentifier(listEl, "filter-option-list")
    if (!list) return

    if (list.hasSearchInputTarget && list.searchInputTarget.value !== "") {
      list.searchInputTarget.value = ""
      list.search()
    }
    list.rehome()
  }

  syncTypeFiltersFromChip() {
    this.typeFilters = new Set(this.readTypeFiltersFromChip())
  }

  readTypeFiltersFromChip() {
    if (!this.hasTypeFilterChipTarget) return []

    return [...this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]:checked')]
      .map((input) => input.value)
      .filter(Boolean)
  }

  isFilterOptionDisabled(element) {
    return element?.getAttribute("aria-disabled") === "true"
      || element?.classList?.contains("is-disabled")
  }

  applyFilters() {
    this.updateFilterAvailability()
    this.updateDeviceCount()
    this.updateFilterChipActiveState()
    this.rebuildFilterVisibleNodeKeys()

    // Force a full re-evaluate so filtered-out nodes drop immediately.
    for (const key of [...this.renderedNodeIds]) {
      this.removeNodeByKey(key)
    }
    this.graph.getLinks().forEach((link) => link.remove())
    this.linkCells.clear()
    this.renderedLinkKeys.clear()

    // Empty copy says "these filters" — only show it when Sync/Type are active.
    // Idle filters on a network-only map (networks excluded from device counts)
    // must not blank the canvas.
    const filtersActive = this.syncFilter !== "all" || this.typeFilters.size > 0
    const hasMatches = !filtersActive || this.hasMatchingDevices(this.syncFilter, this.typeFilters)
    this.setEmptyFilterVisible(!hasMatches)
    this.saveFiltersToStorage()

    if (!hasMatches) {
      // Avoid fitToView zooming into a lone blank canvas.
      return
    }

    // Pack remaining nodes so filters don't leave large empty gaps (Auvik-style).
    this.relayoutNetworkForFilters()
    this.sizeTree()
    this.fitToView()
    this.syncViewport()
    this.schedulePinRenderedGraphToTop()
  }

  setEmptyFilterVisible(visible) {
    if (!this.hasEmptyFilterTarget) return
    this.emptyFilterTarget.hidden = !visible
  }

  // Trailing count of countable devices (networks excluded). Idle → "N devices";
  // with Sync/Type active → "M of N devices".
  updateDeviceCount() {
    if (!this.hasDeviceCountTarget) return

    const counts = this.collectFilterMatchCounts()
    const total = this.countMatchingDevices("all", new Set(), counts)
    const filtersActive = this.syncFilter !== "all" || this.typeFilters.size > 0

    if (!filtersActive) {
      this.deviceCountTarget.textContent = total === 1 ? "1 device" : `${total} devices`
      return
    }

    const matched = this.countMatchingDevices(this.syncFilter, this.typeFilters, counts)
    const noun = total === 1 ? "device" : "devices"
    this.deviceCountTarget.textContent = `${matched} of ${total} ${noun}`
  }

  countMatchingDevices(
    syncFilter = this.syncFilter,
    typeFilters = this.typeFilters,
    counts = this.collectFilterMatchCounts(),
  ) {
    let total = 0

    for (const [type, entry] of counts) {
      if (typeFilters.size > 0 && !typeFilters.has(type)) continue
      if (syncFilter === "synced") total += entry.synced
      else if (syncFilter === "unsynced") total += entry.unsynced
      else total += entry.synced + entry.unsynced
    }

    return total
  }

  updateFilterChipActiveState() {
    const filtersActive = this.syncFilter !== "all" || this.typeFilters.size > 0

    if (this.hasSyncFilterChipTarget) {
      this.updateSyncFilterChipTriggerLabel()
    }
    if (this.hasTypeFilterChipTarget) {
      // Clear All (and other applyFilters paths) bypass filter-chip selection
      // events — keep the Device type trigger label/count in sync here.
      this.updateTypeFilterChipTriggerLabel()
    }
    if (this.hasClearAllTarget) {
      this.clearAllTarget.hidden = !filtersActive
    }
  }

  // Reset Sync + Device type (same as dismissing both chips).
  clearAllFilters(event) {
    event?.preventDefault?.()

    // Clear All is about to hide; keep keyboard focus on the Sync chip trigger
    // (same idea as FilterChip dismiss) instead of dropping to <body>.
    const restoreFocusToSyncChip = this.hasClearAllTarget
      && document.activeElement === this.clearAllTarget

    this.applySyncFilterToChip("all")
    if (this.hasTypeFilterChipTarget) {
      this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false
      })
    }

    this.syncFiltersFromChips()
    this.rehomeSyncFilterList()
    this.rehomeTypeFilterList()
    this.applyFilters()

    if (restoreFocusToSyncChip) this.focusSyncFilterChipTrigger()
  }

  focusSyncFilterChipTrigger() {
    if (!this.hasSyncFilterChipTarget) return

    const trigger = this.syncFilterChipTarget.querySelector(
      "[data-filter-chip-target='dismissFocus'], .filter-chip__trigger",
    )
    trigger?.focus?.()
  }

  // Disable sync/type options that would yield zero devices given the other filters.
  // Unavailable rows stay muted with a (0) count — no inline helper copy.
  updateFilterAvailability() {
    const counts = this.collectFilterMatchCounts()

    if (this.hasSyncFilterChipTarget) {
      this.updateSyncOptionCounts(counts)
      this.syncFilterChipTarget.querySelectorAll('[role="option"][data-value]').forEach((row) => {
        const sync = row.dataset.value
        const selected = row.getAttribute("aria-selected") === "true"
        const available = selected
          || this.syncFilterHasMatches(sync, this.typeFilters, counts)
        this.setSyncOptionAvailable(row, available)
      })
    }

    if (this.hasTypeFilterChipTarget) {
      this.updateTypeOptionCounts(counts)
      this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const type = input.value
        if (!type) return
        const row = input.closest(".filter-option-list__item") || input.closest("li")
        const available = this.typeFilters.has(type)
          || this.typeFilterHasMatches(type, this.syncFilter, counts)
        this.setTypeOptionAvailable(row, input, available)
      })
    }

    // Roving tabindex may still sit on a newly disabled row — move it off.
    this.refreshFilterOptionListRovingTabindex()
  }

  refreshFilterOptionListRovingTabindex() {
    ;[
      this.hasSyncFilterChipTarget && this.syncFilterChipTarget,
      this.hasTypeFilterChipTarget && this.typeFilterChipTarget,
    ].filter(Boolean).forEach((wrap) => {
      const listEl = wrap.querySelector("[data-controller~='filter-option-list']")
      if (!listEl) return
      const list = this.application.getControllerForElementAndIdentifier(
        listEl,
        "filter-option-list",
      )
      list?.refreshRovingTabindex?.()
    })
  }

  // Right-aligned option counts for Sync status, narrowed by active Type filters.
  updateSyncOptionCounts(counts = this.collectFilterMatchCounts()) {
    if (!this.hasSyncFilterChipTarget) return

    let synced = 0
    let unsynced = 0
    for (const [type, entry] of counts) {
      if (this.typeFilters.size > 0 && !this.typeFilters.has(type)) continue
      synced += entry.synced
      unsynced += entry.unsynced
    }

    this.setFilterOptionCount(
      this.syncFilterChipTarget.querySelector('[role="option"][data-value="synced"]'),
      synced,
    )
    this.setFilterOptionCount(
      this.syncFilterChipTarget.querySelector('[role="option"][data-value="unsynced"]'),
      unsynced,
    )
  }

  // Device type row counts, narrowed by the active Sync status filter.
  updateTypeOptionCounts(counts = this.collectFilterMatchCounts()) {
    if (!this.hasTypeFilterChipTarget) return

    this.typeFilterChipTarget.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const type = input.value
      if (!type) return
      const entry = counts.get(type) || { synced: 0, unsynced: 0 }
      let total = entry.synced + entry.unsynced
      if (this.syncFilter === "synced") total = entry.synced
      else if (this.syncFilter === "unsynced") total = entry.unsynced
      const row = input.closest(".filter-option-list__item") || input.closest("li")
      this.setFilterOptionCount(row, total)
    })
  }

  setFilterOptionCount(row, count) {
    if (!row) return

    let el = row.querySelector(".filter-option-list__count")
    if (!el) {
      el = document.createElement("span")
      el.className = "filter-option-list__count"
      const check = row.querySelector(".filter-option-list__check")
      const label = row.querySelector(".filter-option-list__label")
      if (check) row.insertBefore(el, check)
      else if (label) label.appendChild(el)
      else row.appendChild(el)
    }
    el.textContent = `(${count})`
  }

  // One tree walk → per-type synced/unsynced counts for enable/disable checks.
  // Internet and network are structural map nodes, not countable devices.
  collectFilterMatchCounts() {
    const byType = new Map()

    const walk = (node) => {
      if (!node) return
      if (node.type && node.type !== "internet" && node.type !== "network") {
        let entry = byType.get(node.type)
        if (!entry) {
          entry = { synced: 0, unsynced: 0 }
          byType.set(node.type, entry)
        }
        if (this.isNodeSynced(node)) entry.synced += 1
        else entry.unsynced += 1
      }
      ;(node.children || []).forEach((child) => walk(child))
    }

    walk(this.networkTree)
    return byType
  }

  syncFilterHasMatches(syncFilter, typeFilters, counts = this.collectFilterMatchCounts()) {
    for (const [type, entry] of counts) {
      if (typeFilters.size > 0 && !typeFilters.has(type)) continue
      if (syncFilter === "synced" && entry.synced > 0) return true
      if (syncFilter === "unsynced" && entry.unsynced > 0) return true
      if (syncFilter === "all" && entry.synced + entry.unsynced > 0) return true
    }
    return false
  }

  typeFilterHasMatches(type, syncFilter, counts = this.collectFilterMatchCounts()) {
    const entry = counts.get(type)
    if (!entry) return false
    if (syncFilter === "synced") return entry.synced > 0
    if (syncFilter === "unsynced") return entry.unsynced > 0
    return entry.synced + entry.unsynced > 0
  }

  // Sync single-select rows: grayed and not interactive when unavailable.
  setSyncOptionAvailable(row, available) {
    if (!row) return

    const unavailable = !available
    row.classList.toggle("network-map__filter-option--unavailable", unavailable)
    row.setAttribute("aria-disabled", unavailable ? "true" : "false")
    if (unavailable) {
      row.tabIndex = -1
    }
  }

  // Type list rows: grayed and not interactive when unavailable.
  setTypeOptionAvailable(row, input, available) {
    if (!row || !input) return

    const unavailable = !available && !input.checked
    row.classList.toggle("network-map__filter-option--unavailable", unavailable)
    row.setAttribute("aria-disabled", unavailable ? "true" : "false")
    input.disabled = unavailable
    if (unavailable) {
      input.tabIndex = -1
    } else {
      input.removeAttribute("tabindex")
    }
  }

  clearFilterOptionAvailability() {
    ;[this.hasSyncFilterChipTarget && this.syncFilterChipTarget,
      this.hasTypeFilterChipTarget && this.typeFilterChipTarget]
      .filter(Boolean)
      .forEach((wrap) => {
        wrap.querySelectorAll(".network-map__filter-option--unavailable").forEach((row) => {
          row.classList.remove("network-map__filter-option--unavailable")
          row.removeAttribute("aria-disabled")
        })
        wrap.querySelectorAll('input[type="checkbox"][disabled]').forEach((input) => {
          input.disabled = false
          input.removeAttribute("tabindex")
        })
      })
  }

  // True when at least one non-internet device matches the given filter pair.
  hasMatchingDevices(syncFilter = this.syncFilter, typeFilters = this.typeFilters) {
    return this.syncFilterHasMatches(syncFilter, typeFilters)
  }

  // Re-run the Rails tree layout over only shouldRenderNode() nodes so
  // filtered maps tighten instead of leaving holes at old coordinates.
  relayoutNetworkForFilters() {
    if (!this.networkTree) return

    this.clearLayoutPositions(this.networkTree)
    // Match NetworkMapsController#layout: depth 1, start_x 100.
    this.layoutNetwork(this.networkTree, 1, 100)
  }

  clearLayoutPositions(node) {
    if (!node) return

    delete node.x
    delete node.y
    delete node.depth
    ;(node.children || []).forEach((child) => this.clearLayoutPositions(child))
  }

  // Port of NetworkMapsController#layout_network. Only walks children that
  // still pass the active filters (including ancestor keepers).
  layoutNetwork(node, depth = 0, startX = 0) {
    const spacing = this.nodeSpacing

    node.width = NODE_WIDTH
    node.height = NODE_HEIGHT
    node.depth = depth

    const visibleChildren = (node.children || []).filter((child) => this.shouldRenderNode(child))

    if (visibleChildren.length === 0) {
      if (this.shouldRenderNode(node) || node.type === "internet") {
        node.x = startX
        node.y = depth * spacing
      }
      return NODE_WIDTH
    }

    let currentX = startX
    const childrenWidths = []

    visibleChildren.forEach((child) => {
      const childWidth = this.layoutNetwork(child, depth + 1, currentX)
      childrenWidths.push(childWidth)
      currentX += childWidth + spacing
    })

    const totalChildrenWidth = childrenWidths.reduce((sum, width) => sum + width, 0)
      + spacing * (childrenWidths.length - 1)

    node.x = startX + totalChildrenWidth / 2.0 - node.width / 2.0
    node.y = depth * spacing

    return Math.max(totalChildrenWidth, NODE_WIDTH)
  }

  nodeAllowedByFilters(node) {
    if (!node || node.type === "internet") return true
    // Structural only — match via descendants when filters are active.
    if (node.type === "network") {
      return this.syncFilter === "all" && this.typeFilters.size === 0
    }

    if (this.syncFilter === "synced" && !this.isNodeSynced(node)) return false
    if (this.syncFilter === "unsynced" && this.isNodeSynced(node)) return false

    if (this.typeFilters.size > 0 && !this.typeFilters.has(node.type)) return false

    return true
  }

  hasAllowedDescendant(node) {
    return (node.children || []).some((child) => (
      this.nodeAllowedByFilters(child) || this.hasAllowedDescendant(child)
    ))
  }

  // One bottom-up walk → visible keys for shouldRenderNode during pan/zoom.
  // Cleared (null) when no filters are active so the hot path stays a no-op.
  rebuildFilterVisibleNodeKeys() {
    if (this.syncFilter === "all" && this.typeFilters.size === 0) {
      this.filterVisibleNodeKeys = null
      return
    }

    const keys = new Set()
    const walk = (node) => {
      if (!node) return false
      // Internet is always drawn; walk every child (do not short-circuit — each
      // network under internet must be visited or only the first match survives).
      if (node.type === "internet") {
        let anyOk = false
        for (const child of node.children || []) {
          if (walk(child)) anyOk = true
        }
        return anyOk
      }

      const selfOk = this.nodeAllowedByFilters(node)
      let childOk = false
      for (const child of node.children || []) {
        if (walk(child)) childOk = true
      }
      if (selfOk || childOk) {
        keys.add(this.nodeKey(node))
        return true
      }
      return false
    }

    walk(this.networkTree)
    this.filterVisibleNodeKeys = keys
  }

  // Keep ancestors visible when a filtered descendant matches so links don't
  // disappear (e.g. Unsynced hides synced switches that leaves hang under).
  shouldRenderNode(node) {
    if (!node) return false
    if (node.type === "internet") return true
    if (this.filterVisibleNodeKeys) {
      return this.filterVisibleNodeKeys.has(this.nodeKey(node))
    }
    if (this.nodeAllowedByFilters(node)) return true
    if (node.children?.length) return this.hasAllowedDescendant(node)
    return false
  }

  // A long trunk link can cross the viewport while both of its endpoints sit
  // off-screen, so visibility is decided by the link's own span rather than by
  // whether either end node is currently rendered.
  linkSpanVisible(source, target, viewport) {
    return Math.max(source.x, target.x) >= viewport.x1
      && Math.min(source.x, target.x) <= viewport.x2
      && Math.max(source.y, target.y) >= viewport.y1
      && Math.min(source.y, target.y) <= viewport.y2
  }

  pruneDetachedLinks(viewport = this.getViewportBounds()) {
    this.graph.getLinks().forEach((link) => {
      const linkKey = link.get('hudu_link_key')
      if (!linkKey) return

      const sep = linkKey.indexOf('->')
      if (sep < 0) return

      const sourceKey = linkKey.slice(0, sep)
      const targetKey = linkKey.slice(sep + 2)
      if (this.renderedNodeIds.has(sourceKey) || this.renderedNodeIds.has(targetKey)) return

      const source = link.get('source')
      const target = link.get('target')
      if (source && target && this.linkSpanVisible(source, target, viewport)) return

      link.remove()
      this.linkCells.delete(linkKey)
      this.renderedLinkKeys.delete(linkKey)
    })
  }

  ensureVisibleLinks(viewport = this.getViewportBounds()) {
    const walk = (node, parent) => {
      if (node.type === 'internet') {
        node.children?.forEach((child) => walk(child, node))
        return
      }

      const key = this.nodeKey(node)
      const nodeVisible = this.renderedNodeIds.has(key)
      const parentIsDevice = parent && parent.type !== 'internet'
      const parentVisible = parentIsDevice && this.renderedNodeIds.has(this.nodeKey(parent))
      // Also draw when the line itself crosses the viewport, even if neither
      // end node is on-screen.
      const spanVisible = parentIsDevice && this.linkSpanVisible(
        { x: parent.x + (parent.width / 2), y: parent.y + (parent.height / 2) },
        { x: node.x + (node.width / 2), y: node.y + (node.height / 2) },
        viewport,
      )

      // Draw if either end is on-screen so connectors aren't clipped mid-pan.
      // Skip filtered-out nodes so links don't point at invisible devices.
      if (
        parentIsDevice
        && this.shouldRenderNode(parent)
        && this.shouldRenderNode(node)
        && (nodeVisible || parentVisible || spanVisible)
      ) {
        this.drawLink(parent, node)
      }

      node.children?.forEach((child) => walk(child, node))
    }

    walk(this.networkTree, null)
  }

  // Calculate visible area bounds for viewport culling
  getViewportBounds() {
    const containerEl = this.paper.el.parentElement ?? this.parentContainer
    const rect = containerEl.getBoundingClientRect()

    if (rect.width <= 0 || rect.height <= 0) {
      return this.getFullGraphViewport()
    }

    const buffer = this.nodeSpacing * this.graphScale
    const topLeft = this.paper.clientToLocalPoint(rect.left - buffer, rect.top - buffer)
    const bottomRight = this.paper.clientToLocalPoint(rect.right + buffer, rect.bottom + buffer)

    return {
      x1: Math.min(topLeft.x, bottomRight.x),
      y1: Math.min(topLeft.y, bottomRight.y),
      x2: Math.max(topLeft.x, bottomRight.x),
      y2: Math.max(topLeft.y, bottomRight.y),
    }
  }

  getFullGraphViewport() {
    const padding = this.nodeSpacing

    return {
      x1: -padding,
      y1: -padding,
      x2: this.graphBBox.width + padding,
      y2: this.graphBBox.height + padding,
    }
  }

  // Check if a node is within the visible viewport bounds
  isBoxVisible(node, viewport) {
    const w = node.width
    const h = node.height

    return !(
      node.x + w < viewport.x1 ||
      node.x > viewport.x2 ||
      node.y + h < viewport.y1 ||
      node.y > viewport.y2
    )
  }

  // Set paper dimensions based on network tree size.
  // Bounding box is derived from raw data rather than graph.getBBox() because
  // viewport culling in syncViewport() skips off-screen nodes, causing getBBox() to
  // undercount the true extent of the graph.
  sizeTree() {
    this.graphBBox = this.getDataBBox(this.networkTree)
    // Match current scale — sizing at scale 1 while already zoomed out (e.g. after
    // applyFilters) left the paper oversized and broke scroll math.
    const { width, height } = this.paperDimensionsForScale(this.graphScale)
    this.paper.setDimensions(width, height)
  }

  // Position the graph at 100% (1 content pixel per 1 screen pixel). Does not
  // fit-to-screen — wide maps show whatever chunk fits at natural size; the user
  // can still zoom out via minScale() (CORE-9830).
  fitToView() {
    const padding = this.contentPadding()
    const contentWidth = this.graphBBox.width + padding
    const contentHeight = this.graphBBox.height + padding
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight

    if (contentWidth <= 0 || contentHeight <= 0) return

    if (containerWidth <= 0 || containerHeight <= 0) {
      this.pendingFitToView = true
      return
    }

    this.pendingFitToView = false

    const newScale = this.clampScale(1)

    // Always re-apply so a prior sizeTree pass cannot leave the wrong paper size
    // when newScale happens to equal graphScale.
    this.graphScale = newScale
    this.paper.scale(newScale, newScale)
    const { width, height } = this.paperDimensionsForScale(newScale)
    this.paper.setDimensions(width, height)

    this.centerGraphInView()
  }

  centerGraphInView() {
    const bbox = this.graphBBox
    const minX = bbox.minX ?? 0
    const minY = bbox.minY ?? 0
    const maxX = bbox.maxX ?? (minX + bbox.width)
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight
    const scale = this.graphScale

    if (containerWidth <= 0 || containerHeight <= 0) return

    const margin = this.panMargin()
    const padding = this.contentPadding()
    const nodeSpanX = (maxX - minX) * scale
    // Horizontal: middle band so left/right pan room stays equal (CORE-9548).
    // Vertical: park the graph after the top pan band; scroll so load shows ~MAP_TOP_INSET_PX
    // of breathing room while still leaving pan room above for zoom-out.
    const middleWidth = Math.max(containerWidth, (bbox.width + padding) * scale)
    const nodesLeft = margin.x + (middleWidth - nodeSpanX) / 2
    const nodesTop = margin.y
    const translateX = nodesLeft - (minX * scale)
    const translateY = nodesTop - (minY * scale)

    this.contentTranslate = { x: translateX, y: translateY }
    this.paper.translate(translateX, translateY)

    this.parentContainer.scrollLeft = margin.x + middleWidth / 2 - containerWidth / 2
    this.parentContainer.scrollTop = Math.max(0, margin.y - MAP_TOP_INSET_PX)
  }

  // After paint: pin via scroll, then re-cull for the new viewport.
  schedulePinRenderedGraphToTop() {
    requestAnimationFrame(() => {
      if (!this.paper?.el) return
      this.pinRenderedGraphToTop()
      this.syncViewport()
    })
  }

  // Topmost laid-out Y among filter-visible nodes. Internet is never drawn, so
  // anchoring on it (or on culled DOM after syncViewport) left empty band / could
  // shove ancestors above paper y=0 when pin adjusted translate.
  hierarchyAnchorMinY() {
    let minY = Infinity
    const walk = (node) => {
      if (!node) return
      if (
        node.type !== "internet"
        && this.shouldRenderNode(node)
        && node.y != null
      ) {
        minY = Math.min(minY, node.y)
      }
      node.children?.forEach(walk)
    }
    walk(this.networkTree)
    return minY
  }

  // Scroll so the hierarchy anchor sits at MAP_TOP_INSET_PX. Adjust scrollTop
  // only (never contentTranslate) so the graph stays inside the top pan band
  // and zoom-out can keep a cursor-anchored scroll position.
  pinRenderedGraphToTop() {
    if (!this.parentContainer) return
    if (this.parentContainer.clientHeight <= 0) return

    const minY = this.hierarchyAnchorMinY()
    if (!Number.isFinite(minY)) return

    const nodeTopInPaper = this.contentTranslate.y + minY * this.graphScale
    const desiredScrollTop = nodeTopInPaper - MAP_TOP_INSET_PX
    this.parentContainer.scrollTop = Math.max(0, desiredScrollTop)
  }

  // Recursively compute the bounding box of all nodes in the data tree,
  // regardless of whether they are currently rendered.
  getDataBBox(node, bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }) {
    if (node.x != null && node.y != null) {
      bounds.minX = Math.min(bounds.minX, node.x)
      bounds.minY = Math.min(bounds.minY, node.y)
      bounds.maxX = Math.max(bounds.maxX, node.x + (node.width  || 0))
      bounds.maxY = Math.max(bounds.maxY, node.y + (node.height || 0))
    }

    if (node.children) {
      for (const child of node.children) {
        this.getDataBBox(child, bounds)
      }
    }

    if (bounds.minX === Infinity) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }
    }

    return {
      minX: bounds.minX,
      minY: bounds.minY,
      maxX: bounds.maxX,
      maxY: bounds.maxY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
    }
  }

  // ===== NODE DRAWING =====

  // Draw device nodes as ONE cell (icon + name). JointJS mouseenter/leave are
  // per-cell, so a single cell means moving between icon and label never blinks.
  // hitArea covers the full bounds (including the visual gap) so empty space
  // between icon and name still counts as "over the node".
  drawDeviceBox(node) {
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part)))
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) : 20
    const gap = 5
    const iconW = node.width
    const iconH = node.height
    const totalW = Math.max(iconW, labelWidth)
    const totalH = iconH + gap + labelHeight
    const bodyX = (totalW - iconW) / 2
    const nameX = (totalW - labelWidth) / 2
    const synced = this.isNodeSynced(node)

    const markup = [{
      tagName: 'rect',
      selector: 'hitArea',
    }, {
      // Opaque map-colored plate under the tint so link lines cannot show through.
      tagName: 'rect',
      selector: 'bodyBase',
    }, {
      tagName: 'rect',
      selector: 'body',
    }, {
      tagName: 'text',
      selector: 'label',
    }, {
      tagName: 'rect',
      selector: 'nameBase',
    }, {
      tagName: 'rect',
      selector: 'nameBody',
    }, {
      tagName: 'text',
      selector: 'nameText',
    }]

    const rect = new shapes.standard.Rectangle({
      hudu_type: node.type,
      hudu_slug: node.slug,
      hudu_node_key: this.nodeKey(node),
      markup,
    })

    rect.position(node.x + iconW / 2 - totalW / 2, node.y)
    rect.resize(totalW, totalH)

    // Unsynced: lighter pads (*--50) + faded icon; synced: *--200 pads.
    // Both use map-solid base + tinted top (see ICON_PAD_TINT_OPACITY).
    const bodyColor = synced
      ? this.getDeviceColor(node.type)
      : this.getUnsyncedDeviceColor(node.type)
    const tintedBody = this.colorWithOpacity(bodyColor)

    const nameColor = this.getDeviceLabelColor(node.type)
    const padGeometry = {
      rx: 12,
      ry: 12,
      width: iconW,
      height: iconH,
      x: bodyX,
      y: 0,
    }

    const attrs = {
      root: synced ? { cursor: "pointer" } : { cursor: "not-allowed" },
      // Transparent but hittable — fill:"none" would let the cursor fall through the gap.
      hitArea: {
        fill: 'transparent',
        stroke: 'none',
        width: totalW,
        height: totalH,
        x: 0,
        y: 0,
      },
      bodyBase: {
        fill: this.mapBackgroundColor(),
        stroke: this.mapBackgroundColor(),
        ...padGeometry,
      },
      body: {
        fill: tintedBody,
        stroke: tintedBody,

        ...padGeometry,
      },
      label: {
        text: this.getDeviceIcon(node.type),
        fontFamily: 'FontAwesome',
        fill: '#242424',
        fontSize: 18,
        opacity: synced ? 1 : this.unsyncedIconOpacity(),
        x: bodyX + iconW / 2,
        y: iconH / 2,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
      },
      nameBase: {
        fill: this.mapBackgroundColor(),
        stroke: this.mapBackgroundColor(),
        rx: 2,
        ry: 2,
        width: labelWidth,
        height: labelHeight,
        x: nameX,
        y: iconH + gap,
      },
      nameBody: {
        fill: this.colorWithOpacity(nameColor),
        stroke: this.colorWithOpacity(nameColor),
        rx: 2,
        ry: 2,
        width: labelWidth,
        height: labelHeight,
        x: nameX,
        y: iconH + gap,
      },
      nameText: {
        text: labelText,
        fill: '#242424',
        fontSize: 12,
        x: nameX + labelWidth / 2,
        y: iconH + gap + labelHeight / 2,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
      },
    }

    rect.attr(attrs)
    rect.addTo(this.graph)
    this.registerNodeCells(node, [rect])
  }

  // Draw network nodes as ONE cell (icon + name on the chip), same reason as
  // drawDeviceBox: per-cell mouseenter/leave must not fire between icon and label.
  // Map-colored bodyBase under the tint keeps orthogonal links from showing through.
  drawNetworkBox(node) {
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part))) + 18
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) + 4 : 22
    const padGeometry = {
      rx: 2,
      ry: 2,
      width: labelWidth,
      height: labelHeight,
      x: 0,
      y: 0,
    }
    const synced = this.isNodeSynced(node)
    // Same synced/unsynced affordances as devices (cursor, pad swatch, icon fade).
    const bodyColor = synced
      ? this.getDeviceColor(node.type)
      : this.getUnsyncedDeviceColor(node.type)
    const tintedFill = this.colorWithOpacity(bodyColor)
    const tintedStroke = this.colorWithOpacity(this.getDeviceHoverColor(node.type))

    const rect = new shapes.standard.Rectangle({
      hudu_type: node.type,
      hudu_slug: node.slug,
      hudu_node_key: this.nodeKey(node),
      markup: [
        { tagName: "rect", selector: "bodyBase" },
        { tagName: "rect", selector: "body" },
        { tagName: "text", selector: "icon" },
        { tagName: "text", selector: "label" },
      ],
    })
    rect.position(node.x + node.width / 2 - labelWidth / 2, node.y)
    rect.resize(labelWidth, labelHeight)
    rect.attr({
      root: synced ? { cursor: "pointer" } : { cursor: "not-allowed" },
      bodyBase: {
        fill: this.mapBackgroundColor(),
        stroke: this.mapBackgroundColor(),
        ...padGeometry,
      },
      body: {
        fill: tintedFill,
        stroke: tintedStroke,
        strokeWidth: 1,
        ...padGeometry,
      },
      icon: {
        text: this.getDeviceIcon(node.type),
        fill: "#242424",
        fontSize: 14,
        fontFamily: "FontAwesome",
        opacity: synced ? 1 : this.unsyncedIconOpacity(),
        x: 12,
        y: labelHeight / 2,
        textAnchor: "middle",
        textVerticalAnchor: "middle",
      },
      label: {
        text: labelText,
        fill: "#242424",
        fontSize: 12,
        x: labelWidth / 2 + 10,
        y: labelHeight / 2,
        textAnchor: "middle",
        textVerticalAnchor: "middle",
      },
    })
    rect.addTo(this.graph)
    this.registerNodeCells(node, [rect])
  }

  // Draw connection lines between network nodes
  drawLink(sourceNode, targetNode) {
    const linkKey = this.linkKey(sourceNode, targetNode)
    if (this.renderedLinkKeys.has(linkKey)) {
      // Guard against a stale key after a link was removed without clearing the set.
      if (this.linkCells.has(linkKey)) return
      this.renderedLinkKeys.delete(linkKey)
    }

    const link = new shapes.standard.Link({
      source: { x: sourceNode.x + (sourceNode.width / 2), y: sourceNode.y + (sourceNode.height / 2) },
      target: { x: targetNode.x + (targetNode.width / 2), y: targetNode.y + (targetNode.height / 2) }
    })
    
    link.router('orthogonal', { radius: 12 })
    link.connector('rounded')
    link.attr({
      line: {
        stroke: '#D1D1D1',
        strokeWidth: 1,
        sourceMarker: null,
        targetMarker: null,
      }
    })
    link.set('hudu_link_key', linkKey)
    link.addTo(this.graph)
    link.toBack()
    this.linkCells.set(linkKey, link)
    this.renderedLinkKeys.add(linkKey)
  }

  // Compose a node's display label lines. On the merged multi-collector map the
  // collector name arrives as its own field rather than baked into `name`, so it is
  // appended here. Splitting `name` is retained so maps from a hudu-services version
  // that still embeds the newline keep rendering correctly.
  labelLines(node) {
    const lines = String(node.name ?? '').split('\n')
    if (node.deployment_name) lines.push(`(${node.deployment_name})`)
    return lines
  }

  // Measure text width for label sizing
  measureText(svgDocument, text) {
    const vText = V('text').attr({fontSize: 12}).text(text)
    vText.appendTo(svgDocument)
    const bbox = vText.getBBox()
    vText.remove()
    return bbox.width + 8
  }

  // ===== STYLING =====

  // Synced = linked into Hudu (has a slug). Unsynced = discovered only (no slug).
  isNodeSynced(node) {
    return Boolean(node?.slug)
  }

  syncedHoverTooltip(type) {
    return type === "network" ? SYNCED_NETWORK_HOVER_TOOLTIP : SYNCED_HOVER_TOOLTIP
  }

  unsyncedIconOpacity() {
    return UNSYNCED_ICON_OPACITY
  }

  // Lighter system --50 pad for unsynced devices (full opacity; icon fades separately).
  getUnsyncedDeviceColor(type) {
    return UNSYNCED_DEVICE_COLORS[type] || this.getDeviceColor(type)
  }

  // Get FontAwesome icon for device type
  getDeviceIcon(type) {
    const icons = {
      "network": "\uf6ff",
      "access_point": "\uf519",
      "firewall": "\ue3dc",
      "switch": "\uf796",
      "router": "\uf8da",
      "printer": "\uf02f",
      "laptop": "\uf109",
      "workstation": "\ue4e5",
      "server": "\uf233",
      "mobile": "\uf3ce",
      "network_device": "\uf5ef"
    }
    return icons[type]
  }

  // Get background color for device type
  getDeviceColor(type) {
    const colors = {
      "network": "EDE7F6BF",
      "access_point": "CE93D8",
      "firewall": "C5E1A5",
      "switch": "F48FB1",
      "router": "E6EE9C",
      "printer": "9FA8DA",
      "laptop": "A0D5D0",
      "workstation": "80DEEA",
      "server": "81D4FA",
      "mobile": "FFCC80",
      "network_device": "FFAB91"
    }
    return colors[type]
  }

  // Get label background color for device type
  getDeviceLabelColor(type) {
    const labelColors = {
      "network": "F0ECF7",
      "access_point": "F3E5F5BF",
      "firewall": "F1F8E9BF",
      "switch": "FCE4ECBF",
      "router": "F9FBE7BF",
      "printer": "E8EAF6BF",
      "laptop": "E0F2F1BF",
      "workstation": "E0F7FABF",
      "server": "E1F5FEBF",
      "mobile": "FFF3E0BF",
      "network_device": "FBE9E7BF"
    }
    return labelColors[type]
  }

  // Get hover color for device type
  getDeviceHoverColor(type) {
    const hoverColors = {
      "network": "B39DDB",
      "access_point": "BA68C8",
      "firewall": "AED581",
      "switch": "F06292",
      "router": "DCE775",
      "printer": "7986CB",
      "laptop": "80CBC4",
      "workstation": "4DD0E1",
      "server": "4FC3F7",
      "mobile": "FFB74D",
      "network_device": "FF8A65"
    }
    return hoverColors[type]
  }
}
